"use client"

import {
    BugIcon,
    ChevronsUpDown, CircleUserRound,
    LogOut,
} from "lucide-react"

import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "@/components/ui/avatar"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from "@/components/ui/sidebar"
import {useSelector} from "react-redux";
import {useNavigate} from "react-router";
import {useAuth} from "react-oidc-context";
import axios from "axios";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog.tsx";
import {useState} from "react";
import {Textarea} from "@/components/ui/textarea.tsx";
import {Label} from "@/components/ui/label.tsx";
import {Button} from "@/components/ui/button.tsx";
import {toast} from "sonner";

const REPORT_MAX_LENGTH = 10000;

export function NavUser() {
    const navigate = useNavigate();
    const user = useSelector((state: any) => state.auth.user)

    const {isMobile} = useSidebar()
    const auth = useAuth()

    const userMonogram = user?.firstName?.at(0) + user?.lastName?.at(0);

    const [showReportDialog, setShowReportDialog] = useState(false);
    const [reportedText, setReportedText] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const trimmedText = reportedText.trim();
    const canSubmit = trimmedText.length > 0 && trimmedText.length <= REPORT_MAX_LENGTH && !isSubmitting;

    const closeReportDialog = (open: boolean) => {
        if (isSubmitting) return;
        setShowReportDialog(open);
        if (!open) setReportedText("");
    };

    const submitReport = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        try {
            await axios.post("/api/bug-report", {
                body: trimmedText,
                url: window.location.href,
            });
            toast.success("Hibajelentés elküldve");
            setReportedText("");
            setShowReportDialog(false);
        } catch {
            toast.error("Hiba történt a hibajelentés küldése során");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <><Dialog open={showReportDialog} onOpenChange={closeReportDialog}>
            <DialogContent className={"gap-4"}>
                <DialogHeader>
                    <DialogTitle>Hibabejelentés</DialogTitle>
                    <DialogDescription>
                        Írd le, mi történt. Az üzeneted az aktuális oldal címével és a fiókod adataival együtt kerül elküldésre.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-2">
                    <Label htmlFor="bug-report-body">Leírás</Label>
                    <Textarea
                        id="bug-report-body"
                        className="h-64 resize-none"
                        placeholder="Mit próbáltál csinálni? Mit vártál, és mi történt helyette?"
                        value={reportedText}
                        maxLength={REPORT_MAX_LENGTH}
                        disabled={isSubmitting}
                        autoFocus
                        onChange={(s) => setReportedText(s.target.value)}
                    />
                    <div className="text-muted-foreground text-right text-xs">
                        {reportedText.length} / {REPORT_MAX_LENGTH}
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => closeReportDialog(false)}
                        disabled={isSubmitting}
                    >
                        Mégse
                    </Button>
                    <Button onClick={submitReport} disabled={!canSubmit}>
                        {isSubmitting ? "Küldés..." : "Küldés"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
            <SidebarMenu>
                <SidebarMenuItem>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <SidebarMenuButton
                                size="lg"
                                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                            >
                                <Avatar className="h-8 w-8 rounded bg-black">
                                    <AvatarImage src={user?.avatar} alt={user?.firstName + " " + user?.lastName}/>
                                    <AvatarFallback
                                        className="rounded-md bg-black text-sidebar-foreground">{userMonogram}</AvatarFallback>
                                </Avatar>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span
                                        className="truncate font-medium">{user?.firstName + " " + user?.lastName}</span>
                                    <span className="truncate text-xs">{user?.email}</span>
                                </div>
                                <ChevronsUpDown className="ml-auto size-4"/>
                            </SidebarMenuButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                            side={isMobile ? "bottom" : "right"}
                            align="end"
                            sideOffset={4}
                        >
                            <DropdownMenuLabel className="p-0 font-normal">
                                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                    <Avatar className="h-8 w-8 rounded-lg">
                                        <AvatarImage src={user?.avatar} alt={user?.firstName + " " + user?.lastName}/>
                                        <AvatarFallback
                                            className="rounded-lg">{userMonogram}</AvatarFallback>
                                    </Avatar>
                                    <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span
                                        className="truncate font-medium">{user?.firstName + " " + user?.lastName}</span>
                                        <span className="truncate text-xs">{user?.email}</span>
                                    </div>
                                </div>
                            </DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => navigate('/database/account')}>
                                <CircleUserRound/>
                                Fiók
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                                setShowReportDialog(true)
                            }}>
                                <>
                                    <BugIcon/>
                                    Hibajelentés
                                </>

                            </DropdownMenuItem>

                            <DropdownMenuSeparator/>
                            <DropdownMenuItem onClick={() => {
                                auth.signoutRedirect({post_logout_redirect_uri: window.location.origin})
                            }}>
                                <LogOut/>
                                Kilépés
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </SidebarMenuItem>
            </SidebarMenu>
        </>
    )
}
